import { useCallback, useState, useSyncExternalStore } from "react";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";

// Deliberately memory-only: closing a popup must not discard a draft, and
// signing out must not leave its contents on disk or in another account.
const drafts = new Map<string, string>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());
subscribeDesktopChatAuthScope(() => {
  drafts.clear();
  notify();
});
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTransientComposerDraft(identity: string, retain = true) {
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    getDesktopChatAuthScope,
    () => null,
  );
  const key = JSON.stringify([scope?.accountId, scope?.epoch, identity]);
  const persistent = retain && scope !== null;
  const cached = useSyncExternalStore(subscribe, () => drafts.get(key) ?? "", () => "");
  const [local, setLocal] = useState({ key, value: "" });
  const value = persistent ? cached : local.key === key ? local.value : "";
  const setValue = useCallback((next: string, expected?: string) => {
    // An old request finishing after sign-out must not repopulate the cache.
    if (getDesktopChatAuthScope() !== scope) return;
    if (persistent) {
      if (expected !== undefined && (drafts.get(key) ?? "") !== expected) return;
      if (next) drafts.set(key, next);
      else drafts.delete(key);
      notify();
    } else {
      setLocal((current) => expected !== undefined && current.key === key && current.value !== expected
        ? current : { key, value: next });
    }
  }, [key, persistent, scope]);
  return [value, setValue] as const;
}
